/*
 * Shared product grid card behavior (collection grid + search results).
 * Moved verbatim from main-collection-product-grid.liquid so the search page
 * gets identical swatch behavior: hover image-swap, +N overflow, swatch select,
 * combined-listing image hydration, and card-link retargeting.
 *
 * Exposes: window.mcpgInitCombinedListingCards, window.mcpgInitJudgeMeBadges,
 * window.mcpgReplayCardEntrance (called by facet fetches to re-init after grid swaps).
 * Binds document-delegated listeners once; safe against double-inclusion.
 */
(function () {
  if (window.__mcpgCardBehaviorBound) return;
  window.__mcpgCardBehaviorBound = true;

  const mcpgInitJudgeMeBadges = () => {
    if (typeof window.jdgm?.customizeBadges === 'function') {
      window.jdgm.customizeBadges();
    }
  };

  const mcpgReplayCardEntrance = (root = document) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root.querySelectorAll('.main-featured-product-grid .product-item').forEach((card) => {
      card.style.animation = 'none';
      card.offsetHeight;
      card.style.animation = '';
    });
  };

  /** Combined-listing cards: Liquid only resolves one child per card (all_products cap). */
  const mcpgChildProductImageCache = new Map();

  /**
   * INT-1785347404: /products/{handle}.js always returns featured_image/images[] as the
   * untransformed master URL — that endpoint has no Liquid image_url pass, so this is the
   * one place in the combined-listing swatch flow that needs its own width cap. 1000px
   * matches the width already applied to sibling card images elsewhere on the grid.
   */
  const mcpgCapImageWidth = (url, width = 1000) => {
    if (!url || typeof url !== 'string' || !url.includes('cdn.shopify.com')) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  };

  const mcpgCoerceChildProductImages = (productJson) => {
    if (!productJson) return null;
    const featured = productJson.featured_image || productJson.images?.[0] || '';
    const hover = productJson.images?.[1] || '';
    if (!featured) return null;
    return { imageUrl: mcpgCapImageWidth(featured), hoverImageUrl: mcpgCapImageWidth(hover) || '' };
  };

  const mcpgFetchChildProductImages = async (handle) => {
    const key = String(handle || '').trim();
    if (!key) return null;
    if (mcpgChildProductImageCache.has(key)) return mcpgChildProductImageCache.get(key);
    const root = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(`${root}products/${encodeURIComponent(key)}.js`, {
        credentials: 'same-origin'
      });
      if (!res.ok) {
        mcpgChildProductImageCache.set(key, null);
        return null;
      }
      const data = await res.json();
      const entry = mcpgCoerceChildProductImages(data);
      mcpgChildProductImageCache.set(key, entry);
      return entry;
    } catch {
      mcpgChildProductImageCache.set(key, null);
      return null;
    }
  };

  const mcpgApplyChildImagesToSwatch = (swatch, entry) => {
    if (!swatch || !entry?.imageUrl) return;
    if (!swatch.dataset.imageUrl) swatch.dataset.imageUrl = entry.imageUrl;
    if (!swatch.dataset.hoverImageUrl && entry.hoverImageUrl) {
      swatch.dataset.hoverImageUrl = entry.hoverImageUrl;
    }
  };

  const mcpgHydrateCombinedListingSwatchImages = async (scopeRoot = document) => {
    const swatches = scopeRoot.querySelectorAll(
      '.mcpg-card-swatches[data-swatch-source="combined-listing"] .mcpg-card-swatch[data-product-handle]'
    );
    if (!swatches.length) return;

    const handles = new Set();
    swatches.forEach((swatch) => {
      if (!swatch.dataset.imageUrl && swatch.dataset.productHandle) {
        handles.add(swatch.dataset.productHandle);
      }
    });
    if (!handles.size) return;

    await Promise.all([...handles].map((handle) => mcpgFetchChildProductImages(handle)));

    swatches.forEach((swatch) => {
      const entry = mcpgChildProductImageCache.get(swatch.dataset.productHandle);
      mcpgApplyChildImagesToSwatch(swatch, entry);
    });
  };

  const mcpgEnsureSwatchImages = async (swatch) => {
    if (!swatch || swatch.dataset.imageUrl || !swatch.dataset.productHandle) return;
    const entry = await mcpgFetchChildProductImages(swatch.dataset.productHandle);
    mcpgApplyChildImagesToSwatch(swatch, entry);
  };

  const mcpgBuildCombinedListingPdpUrl = (swatch, card) => {
    if (!swatch) return '';
    if (swatch.dataset.pdpUrl) return swatch.dataset.pdpUrl;
    const parentUrl =
      swatch.dataset.parentUrl || card?.dataset?.parentUrl || card?.querySelector('.mcpg-card-swatches')?.dataset?.parentUrl;
    const optionValueId = swatch.dataset.optionValueId;
    const variantId = swatch.dataset.variantId;
    if (!parentUrl || !optionValueId) return parentUrl || '';
    try {
      const u = new URL(parentUrl, window.location.origin);
      u.searchParams.set('option_values', optionValueId);
      if (variantId) u.searchParams.set('variant', variantId);
      return `${u.pathname}${u.search}`;
    } catch {
      return parentUrl;
    }
  };

  const mcpgApplyCardImages = (card, primaryUrl, hoverUrl) => {
    const primary = card.querySelector('.primary-image');
    const secondary = card.querySelector('.secondary-image');
    if (primary && primaryUrl) {
      primary.src = primaryUrl;
      primary.removeAttribute('srcset');
      primary.removeAttribute('sizes');
    }
    if (secondary && hoverUrl) {
      secondary.src = hoverUrl;
      secondary.removeAttribute('srcset');
      secondary.removeAttribute('sizes');
    }
  };

  const mcpgPaintCardSwatch = (card, swatch) => {
    if (!card || !swatch) return;
    const primaryUrl = swatch.dataset.imageUrl || '';
    const hoverUrl = swatch.dataset.hoverImageUrl || '';
    if (!primaryUrl && swatch.dataset.productHandle) {
      void mcpgEnsureSwatchImages(swatch).then(() => {
        if (swatch.dataset.imageUrl) mcpgApplyCardImages(card, swatch.dataset.imageUrl, swatch.dataset.hoverImageUrl || '');
      });
      return;
    }
    mcpgApplyCardImages(card, primaryUrl, hoverUrl);
  };

  const mcpgGetActiveSwatch = (card) => card.querySelector('.mcpg-card-swatch.active');

  const mcpgMoveActiveSwatchFirst = (wrap, swatch) => {
    if (!wrap || !swatch) return;
    const first = wrap.querySelector('.mcpg-card-swatch');
    if (first && first !== swatch) {
      wrap.insertBefore(swatch, first);
    }
  };

  const mcpgApplyCardUrl = (card, url) => {
    if (!card || !url) return;
    card.dataset.cardUrl = url;
    const imageLink = card.querySelector('.mcpg-card-image-link');
    if (imageLink) imageLink.href = url;
    const titleLink = card.querySelector('.mcpg-card-title-link');
    if (titleLink) titleLink.href = url;
    card.querySelectorAll('.mcpg-card-swatch-more').forEach((link) => {
      link.href = url;
    });
    const btn = card.querySelector('.product-item_bottom-wrap .btn');
    if (btn) btn.setAttribute('onclick', `location.href='${url}'`);
  };

  const mcpgUpdateCardLinks = (card, swatch) => {
    if (!card || !swatch) return;
    const wrap = card.querySelector('.mcpg-card-swatches');
    const mode = wrap?.dataset?.swatchSource || card.dataset.swatchSource || '';
    let url = swatch.dataset.pdpUrl || '';
    if (!url && mode === 'combined-listing') {
      url = mcpgBuildCombinedListingPdpUrl(swatch, card);
    }
    if (
      !url &&
      (mode === 'custom-options' || mode === 'pdp-customizer-background') &&
      swatch.dataset.colorName
    ) {
      try {
        const base = (card.dataset.cardUrl || window.location.pathname).split('?')[0];
        url = `${base}?background=${encodeURIComponent(swatch.dataset.colorName)}`;
      } catch {
        url = '';
      }
    }
    if (!url) url = card.dataset.cardUrl || '';
    if (!url) return;
    mcpgApplyCardUrl(card, url);
  };

  const mcpgCommitCardSwatch = (card, swatch) => {
    const wrap = swatch.closest('.mcpg-card-swatches');
    if (!wrap) return;
    wrap.querySelectorAll('.mcpg-card-swatch').forEach((el) => {
      el.classList.remove('active');
      el.setAttribute('aria-pressed', 'false');
    });
    swatch.classList.add('active');
    swatch.setAttribute('aria-pressed', 'true');
    mcpgMoveActiveSwatchFirst(wrap, swatch);
    mcpgUpdateCardLinks(card, swatch);
    mcpgPaintCardSwatch(card, swatch);
  };

  // Capture the server-rendered (parent PDP) image so we can return to it when no color is selected.
  const mcpgCaptureRestImages = (card) => {
    ['primary-image', 'secondary-image'].forEach((cls) => {
      const img = card.querySelector('.' + cls);
      if (img && img.dataset.restCaptured !== 'true') {
        img.dataset.restSrc = img.getAttribute('src') || '';
        img.dataset.restSrcset = img.getAttribute('srcset') || '';
        img.dataset.restSizes = img.getAttribute('sizes') || '';
        img.dataset.restCaptured = 'true';
      }
    });
  };

  const mcpgRestoreRestImages = (card) => {
    ['primary-image', 'secondary-image'].forEach((cls) => {
      const img = card.querySelector('.' + cls);
      if (!img || img.dataset.restCaptured !== 'true') return;
      if (img.dataset.restSrc) img.src = img.dataset.restSrc;
      if (img.dataset.restSrcset) {
        img.setAttribute('srcset', img.dataset.restSrcset);
      } else {
        img.removeAttribute('srcset');
      }
      if (img.dataset.restSizes) {
        img.setAttribute('sizes', img.dataset.restSizes);
      } else {
        img.removeAttribute('sizes');
      }
    });
  };

  const mcpgClearCardSelection = (card) => {
    card.querySelectorAll('.mcpg-card-swatch').forEach((el) => {
      el.classList.remove('active');
      el.setAttribute('aria-pressed', 'false');
    });
  };

  const mcpgInitCombinedListingCards = async () => {
    await mcpgHydrateCombinedListingSwatchImages();
    document.querySelectorAll('.product-item--has-swatches').forEach((card) => {
      // Endless scrolling re-runs this after each batch; leave cards a shopper already touched alone.
      if (card.dataset.mcpgReady === 'true') return;
      card.dataset.mcpgReady = 'true';
      // Default resting state: no swatch pre-selected and the card shows the parent PDP first
      // image (rendered server-side). The card links point at the parent PDP with the first
      // color's option_values, so opening it auto-selects the first color. A color is selected
      // on the card only when the shopper clicks a swatch (mcpgCommitCardSwatch), which repaints
      // the image and points the card at that variant's PDP.
      mcpgCaptureRestImages(card);
      mcpgClearCardSelection(card);
      mcpgRestoreRestImages(card);
      const wrap = card.querySelector('.mcpg-card-swatches');
      // Default (no swatch clicked): point the card at the BARE parent PDP url (strip the
      // first color's ?option_values) so a plain card click opens the parent shell with NO
      // color pre-selected. option_values is only added when the shopper clicks a swatch
      // (mcpgCommitCardSwatch → mcpgUpdateCardLinks).
      const initialUrl = wrap && wrap.dataset ? wrap.dataset.initialPdpUrl : '';
      const bareUrl = initialUrl ? initialUrl.split('?')[0] : '';
      if (bareUrl) mcpgApplyCardUrl(card, bareUrl);
    });
  };

  const mcpgBootCombinedListingCards = () => {
    void mcpgInitCombinedListingCards();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      mcpgBootCombinedListingCards();
      mcpgInitJudgeMeBadges();
    });
  } else {
    mcpgBootCombinedListingCards();
    mcpgInitJudgeMeBadges();
  }
  window.mcpgInitCombinedListingCards = mcpgBootCombinedListingCards;
  window.mcpgInitJudgeMeBadges = mcpgInitJudgeMeBadges;
  window.mcpgReplayCardEntrance = mcpgReplayCardEntrance;

  document.addEventListener(
    'mouseenter',
    (event) => {
      // `mouseenter` fires on `document` when the pointer enters the viewport; then
      // `event.target` is the document, which has no `.closest` — guard before calling it.
      if (!(event.target instanceof Element)) return;
      const swatch = event.target.closest('.mcpg-card-swatch');
      if (!swatch) return;
      const card = swatch.closest('.product-item--has-swatches');
      if (!card) return;
      mcpgCaptureRestImages(card);
      mcpgPaintCardSwatch(card, swatch);
    },
    true
  );

  document.addEventListener('focusin', (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('.mcpg-card-swatch');
    if (!swatch) return;
    const card = swatch.closest('.product-item--has-swatches');
    if (card) mcpgPaintCardSwatch(card, swatch);
  });

  document.addEventListener('mouseleave', (event) => {
    // `mouseleave` fires on `document` when the pointer leaves the viewport; `event.target`
    // is then the document (no `.closest`). Guard so it no-ops instead of throwing.
    if (!(event.target instanceof Element)) return;
    const wrap = event.target.closest('.mcpg-card-swatches');
    if (!wrap || wrap.contains(event.relatedTarget)) return;
    const card = wrap.closest('.product-item--has-swatches');
    if (!card) return;
    const active = mcpgGetActiveSwatch(card);
    if (active) {
      mcpgPaintCardSwatch(card, active);
    } else {
      mcpgRestoreRestImages(card);
    }
  });

  document.addEventListener('focusout', (event) => {
    if (!(event.target instanceof Element)) return;
    const wrap = event.target.closest('.mcpg-card-swatches');
    if (!wrap || wrap.contains(event.relatedTarget)) return;
    const card = wrap.closest('.product-item--has-swatches');
    if (!card) return;
    const active = mcpgGetActiveSwatch(card);
    if (active) {
      mcpgPaintCardSwatch(card, active);
    } else {
      mcpgRestoreRestImages(card);
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('.mcpg-card-swatch');
    if (swatch) {
      event.preventDefault();
      event.stopPropagation();
      const card = swatch.closest('.product-item--has-swatches');
      if (card) mcpgCommitCardSwatch(card, swatch);
      return;
    }
    const moreLink = event.target.closest('.mcpg-card-swatch-more');
    if (moreLink) {
      event.stopPropagation();
      return;
    }
    const imageLink = event.target.closest('.mcpg-card-image-link');
    if (imageLink) {
      event.stopPropagation();
      return;
    }
    const titleLink = event.target.closest('.mcpg-card-title-link');
    if (titleLink) {
      event.stopPropagation();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('.mcpg-card-swatch');
    if (!swatch) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      const card = swatch.closest('.product-item--has-swatches');
      if (card) mcpgCommitCardSwatch(card, swatch);
    }
  });
})();
